import {Injectable} from '@angular/core';
import {firstValueFrom, forkJoin, lastValueFrom, mergeAll, mergeMap, Observable, of} from 'rxjs';
import {ActivatedRoute} from '@angular/router';
import {MitgliedService} from '@wba/services/api/mitglied/mitglied.service';
import {MitgliederDomainDto} from '@wba/models/dto/mitglieder-domain-dto';
import {AbstractApiService} from '@wba/services/commons/abstract-api.service';
import {HttpClient} from '@angular/common/http';
import {KontoauszugDto, NachweiseKontoDto} from '@wba/models/dto/konto-dto';
import {NachweisService} from '@wba/services/api/nachweis/nachweis.service';
import {AnlageTyp} from '@wba/models/enums/anlage-typ.enum';
import {AntragsdatenService} from '@wba/services/api/antragsdaten/antragsdaten.service';
import {AntragsdatenSchluessel} from '@wba/models/enums/antragsdaten-schluessel.enum';
import {AntragsdatenDto} from '@wba/models/dto/antragsdaten-dto';
import {map, shareReplay, switchMap, toArray} from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class KontoauszuegeService extends AbstractApiService {
  constructor(
    protected http: HttpClient,
    private readonly route: ActivatedRoute,
    private readonly mitgliedService: MitgliedService,
    private readonly nachweisService: NachweisService,
    private readonly antragsdatenService: AntragsdatenService
  ) {
    super(http);
  }

  getMitglieder() {
    return this.mitgliedService.getMitglieder(this.route.snapshot.queryParams.id);
  }

  putMitglied(mitglied, bg) {
    const mitgliedObj: MitgliederDomainDto = new MitgliederDomainDto();
    mitgliedObj.mitglieder.push(mitglied);

    if (mitglied.isAntragsteller) {
      mitgliedObj.bg.hatKostenUnterkunft = bg.hatKostenUnterkunft;
      mitgliedObj.bg.hatUmzug = bg.hatUmzug;
      mitgliedObj.bg.letzteBewilligung = bg.letzteBewilligung;
    }

    return this.mitgliedService.updateMitglieder(mitgliedObj, this.route.snapshot.queryParams.id, false);
  }

  private mapAntragsdatenDtoToKontoDto(antragsdaten: AntragsdatenDto): NachweiseKontoDto {
    const konto = antragsdaten.jsondaten as NachweiseKontoDto;
    konto.id = antragsdaten.id;
    return konto;
  }

  private buildKonto(mitgliedId: string): NachweiseKontoDto {
    return {
      inhaber: mitgliedId,
      nachweise: []
    } as NachweiseKontoDto;
  }

  createKontoDraft(mitgliedId: string): Observable<NachweiseKontoDto> {
    return this.antragsdatenService.createDraft({
        jsondaten: this.buildKonto(mitgliedId) as any,
        schluessel: AntragsdatenSchluessel.DRAFT_KONTO
      } as AntragsdatenDto,
      this.route.snapshot.queryParams.id)
      .pipe(
        map(antragsdaten => this.mapAntragsdatenDtoToKontoDto(antragsdaten))
      );
  }

  updateKontoDraft(konto: NachweiseKontoDto): Observable<NachweiseKontoDto> {
    return this.antragsdatenService.updateDraft(
      {id: konto.id, schluessel: AntragsdatenSchluessel.DRAFT_KONTO, jsondaten: konto as any} as AntragsdatenDto,
      this.route.snapshot.queryParams.id)
      .pipe(
        map(antragsdaten => this.mapAntragsdatenDtoToKontoDto(antragsdaten))
      );
  }

  updateKonto(konto: NachweiseKontoDto) {
    return this.patch<NachweiseKontoDto>('/konto', konto, {antragId: this.route.snapshot.queryParams.id});
  }

  createOrUpdateKonto(konto: NachweiseKontoDto): Observable<NachweiseKontoDto> {
    return this.put<NachweiseKontoDto>(`/konto/${konto.id}`, konto, {antragId: this.route.snapshot.queryParams.id});
  }

  createKonto(mitgliedId: string): Observable<NachweiseKontoDto> {
    return this.post<NachweiseKontoDto>('/konto', {}, {
      antragId: this.route.snapshot.queryParams.id,
      mitgliedId: mitgliedId
    });
  }

  async createOrUpdateKontosFromKontoDrafts(mitgliedId: string) {
    const kontoDrafts = await lastValueFrom(this.getKontoDrafts(mitgliedId));
    await Promise.all(
      kontoDrafts.map(kontoDraft => lastValueFrom(this.createOrUpdateKonto(kontoDraft))
        .then(kontoDraftToDelete => lastValueFrom(this.deleteKontoDraft(kontoDraftToDelete.id)))
      )
    )
  }

  getKontos(mitgliedId: string): Observable<NachweiseKontoDto[]> {
    return this.get('/konto', {antragId: this.route.snapshot.queryParams.id, mitgliedId: mitgliedId});
  }

  getKonto(kontoId: string): Observable<NachweiseKontoDto> {
    return this.get(`/konto/${kontoId}`, {antragId: this.route.snapshot.queryParams.id});
  }

  getKontoDraft(kontoDraftId: string): Observable<NachweiseKontoDto> {
    return this.antragsdatenService.getDraft(this.route.snapshot.queryParams.id, AntragsdatenSchluessel.DRAFT_KONTO, kontoDraftId).pipe(
      map(antragsdaten => this.mapAntragsdatenDtoToKontoDto(antragsdaten))
    );
  }

  getKontoDrafts(mitgliedId: string): Observable<NachweiseKontoDto[]> {
    return this.antragsdatenService.getDrafts(this.route.snapshot.queryParams.id, true, AntragsdatenSchluessel.DRAFT_KONTO).pipe(
      shareReplay(1),
      map(antragsdatenList => antragsdatenList
        .filter(antragsdaten => (antragsdaten.jsondaten as NachweiseKontoDto).inhaber === mitgliedId)
        .map(antragsdaten => this.mapAntragsdatenDtoToKontoDto(antragsdaten))
      )
    );
  }

  private createMissingKontoDraftsForExistingKontos(existingKontos$: Observable<NachweiseKontoDto[]>, kontoDrafts$: Observable<NachweiseKontoDto[]>): Observable<NachweiseKontoDto[]> {
    return forkJoin([existingKontos$, kontoDrafts$]).pipe(
      map(([kontos, kontoDrafts]) => kontos
        .filter(konto => kontoDrafts
          .findIndex(kontoDraft => kontoDraft.id === konto.id) < 0
        )
      ),
      mergeMap(kontos => kontos.map(konto => this.updateKontoDraft(konto))),
      mergeAll(),
      toArray()
    );
  }

  getOrCreateKontoDrafts(mitgliedId: string): Observable<NachweiseKontoDto[]> {
    let kontoDrafts$ = this.getKontoDrafts(mitgliedId);
    let kontos$ = this.getKontos(mitgliedId);
    let newKontoDraftsForExistingKontos$ = this.createMissingKontoDraftsForExistingKontos(kontos$, kontoDrafts$);
    return forkJoin([kontoDrafts$, newKontoDraftsForExistingKontos$]).pipe(
      map(([kontoDrafts, newKontoDraftsForExistingKontos]) => {
        return kontoDrafts.concat(newKontoDraftsForExistingKontos);
      }),
    );
  }

  deleteKonto(kontoId: string): Observable<any> {
    return this.delete('/konto/' + kontoId, {antragId: this.route.snapshot.queryParams.id});
  }

  deleteKontos(mitgliedId: string): Observable<any> {
    return this.delete('/konto', {antragId: this.route.snapshot.queryParams.id, mitgliedId: mitgliedId});
  }

  deleteKontoDraft(kontoId: string): Observable<any> {
    return this.antragsdatenService.deleteDraft(this.route.snapshot.queryParams.id, AntragsdatenSchluessel.DRAFT_KONTO, kontoId);
  }

  deleteKontoAndKontoDraftWithNachweiseForKontoId(kontoId: string): Observable<any> {
    return this.getKontoDraft(kontoId).pipe(
      map(kontoDraft => {
        lastValueFrom(this.getKonto(kontoId))
          .then(konto => {
            Promise.all(this.deleteKontoAndKontoDraftWithNachweise(konto, kontoDraft))
              .catch(error => of(error.message));
          }).catch(error => of(error.message));
      })
    )
  }

  deleteAllKontosAndKontoDraftsWithNachweise(mitgliedId: string): Promise<void> {
    return lastValueFrom(this.getKontoDrafts(mitgliedId))
      .then(kontoDrafts => kontoDrafts.forEach(kontoDraft => {
        lastValueFrom(this.getKonto(kontoDraft.id))
          .then(konto => {
            Promise.all(this.deleteKontoAndKontoDraftWithNachweise(konto, kontoDraft))
              .catch(error => { throw error; });
          }).catch(error => { throw error; });
      })).catch(error => { throw error; });
  }

  // Nachweise an KontoDrafts sind selbst keine Drafts. Die Verbindung zwischen KontoDraft und Nachweisen entsteht ueber
  // die nachweise-Liste am KontoDraft. Nachweise, die nur am KontoDraft existieren, muessen separat geloescht werden.
  deleteKontoAndKontoDraftWithNachweise(konto: NachweiseKontoDto, kontoDraft: NachweiseKontoDto): Promise<any>[] {
    let promises: Promise<any>[] = [];
    this.getNachweiseOnlyInKontoDraft(kontoDraft, konto)
      .forEach(nachweis => promises.push(lastValueFrom(this.deleteNachweis(nachweis.id))));
    promises.push(lastValueFrom(this.deleteKontoDraft(kontoDraft.id)));
    if (!!konto) {
      promises.push(lastValueFrom(this.deleteKonto(konto.id)));
    }
    return promises;
  }

  private getNachweiseOnlyInKontoDraft(kontoDraft: NachweiseKontoDto, konto: NachweiseKontoDto): KontoauszugDto[] {
    if (!!kontoDraft && kontoDraft.nachweise && kontoDraft.nachweise.length > 0) {
      if (!!konto && konto.nachweise && konto.nachweise.length > 0) {
        return kontoDraft.nachweise.filter(draftNachweis =>
          konto.nachweise.findIndex(nachweis => draftNachweis.id === nachweis.id) < 0
        );
      } else {
        return kontoDraft.nachweise;
      }
    } else {
      return [];
    }
  }

  deleteNachweis(nachweisId: string) {
    return this.nachweisService.deleteFile(this.route.snapshot.queryParams.id, nachweisId, AnlageTyp.KONTOAUSZUEGE);
  }

  cleanupInvalidNachweiseInStorage(): Promise<void> {
    return firstValueFrom(this.antragsdatenService.patchDraftJsondatenCleanup(this.route.snapshot.queryParams.id, AntragsdatenSchluessel.DRAFT_KONTO));
  }

  cleanupOrphanedNachweise() {
    return firstValueFrom(this.nachweisService.cleanupNachweise(this.route.snapshot.queryParams.id));
  }

  deleteNachweisFromKontoDraft(kontoDraftId: string, nachweisId: string): Observable<NachweiseKontoDto> {
    return this.getKontoDraft(kontoDraftId).pipe(
      switchMap(kontoDraft => {
        let findIndex = kontoDraft.nachweise.findIndex(nachweis => nachweis.id === nachweisId);
        if (findIndex > -1) {
          kontoDraft.nachweise.splice(findIndex, 1);
        }
        return this.updateKontoDraft(kontoDraft);
      })
    );
  }

  deleteAllNachweiseFromKontoDraft(antragsdatenId: string) {
    const antragId = this.route.snapshot.queryParams.id;
    const url = `/antrag/${antragId}/antragsdaten/${antragsdatenId}/konto-nachweise`;
    return this.delete(url);
  }

}
